import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const meta = {
  title: "First-Time Homebuyer's Guide to Home Insurance",
  description: "First-time homebuyer's guide to home insurance: coverage types, costs, and essential tips to protect your investment and avoid costly mistakes.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_497008264-scaled.webp",
    alt: "First-Time Homebuyer's Guide to Home Insurance"
  },
  author: {
    name: "Dash Lewis, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 17, 2025",
  updatedAt: "July 17, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    ol: "ol",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["The path to homeownership has become increasingly challenging for first-time buyers, with their market share dropping to a historic low of 24% in 2024—down from 32% just a year earlier, according to the ", _jsx(_components.a, {
        href: "https://www.nar.realtor/newsroom/first-time-home-buyers-shrink-to-historic-low-of-24-as-buyer-age-hits-record-high",
        children: "National Association of Realtors"
      }), ". As first-time buyers now reach a median age of 38 years (up from 35 last year) and now need a median household income of $97,000 to compete in today’s market. The financial requirements for first-time homebuyers is higher, and so is the premium to protect their home. So fully understanding your ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "homeowners insurance"
      }), " so you’re prepared to pick the right policy at the right price is the best way to protect your investment (your new home)."]
    }), "\n", _jsx(_components.p, {
      children: "There are several steps involved in purchasing a home, including loans, inspections, and assessments. It’s a lot to keep track of. But shopping for first-time home buyer insurance doesn’t have to be a daunting task. In this article, we’ll give an overview of what home insurance is, the types of coverage you can choose from, and tips for finding the right policy as a first-time homebuyer."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "The average annual home insurance premium for $350,000 dwelling coverage with a $1,000 deductible is $2,927."
      }), "\n", _jsx(_components.li, {
        children: "Standard homeowners insurance policies have four primary coverages: structural coverage, other structures coverage, liability coverage, and personal property coverage."
      }), "\n", _jsx(_components.li, {
        children: "Flood insurance is not included in a standard home insurance policy yet 41% of surveyed homeowners believed it was."
      }), "\n", _jsx(_components.li, {
        children: "Homeowners with poor credit pay an average of $4,638 annually for coverage, while those with excellent credit pay just $2,329 per year—a difference of $2,309 that can significantly impact first-time buyers’ budgets."
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "What Is Homeowners Insurance?"
    }), "\n", _jsx(_components.p, {
      children: "In a nutshell, homeowners insurance offers financial protection if your home is damaged by an accident or disaster."
    }), "\n", _jsx(_components.p, {
      children: "If something unexpected happens, a homeowners insurance policy can help cover repairs to your home or other structures on your property, as well as the replacement of damaged or destroyed belongings. Should you experience a sudden or accidental loss, you can file a claim with your insurance provider and receive a payout or reimbursement up to your policy’s coverage limits. Any money paid by the insurance company will come after you’ve hit your policy deductible, which is the amount you pay out of pocket before coverage kicks in."
    }), "\n", _jsx(_components.p, {
      children: "Home insurance differs from car insurance in that it’s not mandated by law. However, most mortgage lenders will require you to purchase at least some coverage. When you take out a mortgage loan, the lender has a financial stake in the property. Having a homeowners policy ensures that the lender will receive a payout in the event of covered damage."
    }), "\n", _jsx(_components.h4, {
      children: "What Does Homeowners Insurance Cover?"
    }), "\n", _jsxs(_components.p, {
      children: ["Standard ", _jsx(_components.a, {
        href: "https://www.ncdoi.gov/consumers/homeowners-insurance/basic-homeowners-insurance",
        children: "homeowners insurance"
      }), " typically includes the following:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Structural coverage,"
        }), " which covers your home itself"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Other structures coverage"
        }), ", which covers other structures on your property, like a shed, fence, or other outbuilding"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Liability coverage"
        }), ", which covers you if an accident or event on your property injures another person or damages their property"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Personal property coverage"
        }), ", which covers your belongings"]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "Understanding the Types of Homeowners Insurance"
    }), "\n", _jsxs(_components.p, {
      children: ["When shopping for a home insurance policy, you’ll need to parse through the various kinds of coverage available to choose what’s best for your needs. There are ", _jsx(_components.a, {
        href: "https://www.progressive.com/homeowners/faq/policy-types/",
        children: "eight main types"
      }), " of homeowners insurance:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-1:"
        }), " These are the most basic home insurance policies and only include coverage for the physical structure of your property and belongings at their actual cash value. Most providers no longer sell these policies."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-2:"
        }), " These are also known as broad form policies, which provide more coverage than the basic HO-1 but less than a standard policy."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-3:"
        }), " This is the most common type of homeowners insurance policy. Sometimes referred to as “special form” policies, HO-3 policies offer all-risk coverage."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-4:"
        }), " These policies are made explicitly for renters and are often called “renters’ insurance.”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-5:"
        }), " These are the second most common home insurance policies and offer the most comprehensive coverage."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-6:"
        }), " This type of coverage is specifically designed for condo owners."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-7:"
        }), " This type of coverage is specifically designed for owners of mobile or manufactured homes."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "HO-8:"
        }), " These policies are designed for old and historic homes with ornate features that cost more to replace than the home’s worth."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["When researching policies, you’ll also notice the terms “", _jsx(_components.a, {
        href: "/home-insurance/insurance-perils",
        children: "named peril"
      }), "” and “open peril.” These refer to the specific circumstances that cause damage or destruction to your property. Here’s what you need to know to differentiate between the two:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Named peril:"
        }), " Policies that have named peril coverage cover damage or loss due to specific events or disasters. Named peril policies, sometimes called “specified peril” policies, have lists of what perils are covered and what are excluded."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Open peril:"
        }), " Also referred to as “all peril,” open peril policies cover damage and loss to your home and belongings if anything happens. These policies still have exclusions, but they’re often fewer in number than those in named peril policies."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "What To Consider When Purchasing Home Insurance"
    }), "\n", _jsxs(_components.p, {
      children: ["There are several key factors to keep in mind when ", _jsx(_components.a, {
        href: "https://www.iii.org/article/home-buyers-insurance-guide",
        children: "shopping for a home and homeowners insurance."
      }), " We’ll break these into three sections: what to consider before you start house hunting, what to assess while house hunting, and what to know when you make an offer."]
    }), "\n", _jsx(_components.h4, {
      children: "What To Consider Before House Hunting"
    }), "\n", _jsx(_components.p, {
      children: "Get your financial affairs in order before shopping for a home."
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Research average premium costs of your new area."
        }), " Take a look at the average premium costs in your area and factor that figure into the annual cost of homeownership, along with maintenance and repair costs to fully understand the financial picture of owning a home."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Check your credit score."
        }), " The better your credit, the more likely it is that you’ll qualify for a mortgage loan with a reasonable interest rate. Some insurance providers offer discounts to clients with high credit scores. If your credit history is spotty, take steps to improve your score."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Purchase renters insurance."
        }), " If you’re renting a home or apartment, protect yourself financially by purchasing a renters insurance policy. This can help protect a down payment on a house should you experience a disaster or accident, and it presents a solid insurance history to a provider."]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "What To Consider While House Hunting"
    }), "\n", _jsx(_components.p, {
      children: "While looking for your new house, take into account all the factors that may make it more expensive to insure."
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Note the location of the nearest fire department."
        }), " Houses closer to fire stations, especially those that are highly rated and continuously staffed, typically cost less to insure. Insurance premiums may also be lower if the house is in close proximity to a fire hydrant."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Check the age of the home."
        }), " If you’re considering buying an older home, research the costs associated with updating the plumbing and electrical systems. Some older homes may be more expensive to insure due to the high cost of replacing features such as wooden floors and plaster walls."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Check the condition of the roof."
        }), " Roofs are expensive to repair and replace, so make sure you know the age and repair history of the roof on your prospective home, as well as what it’s made from. Some insurers offer discounts for roofs made with fire or hail-resistant materials."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Inspect the quality of the house’s construction."
        }), " Homes built to code will have a better chance of withstanding inclement weather and natural disasters, and thus will be less expensive to insure."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Assess the flood risk."
        }), " Most standard home insurance policies do not cover flooding, so if the home you’re looking at sits in a floodplain, you’ll need to factor in the cost of supplemental flood insurance."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Determine the earthquake risk in the area."
        }), " Earthquake damage is not included in standard insurance coverage, so if the area has a history of earthquakes (like Southern California, for example), you may need to purchase supplemental earthquake insurance."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Check if the house has a pool or other special features."
        }), " You may need to purchase additional liability insurance if the house comes with a pool, hot tub, or other special features."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Consider living further from the coastline."
        }), " Insurers will charge more for policies on homes closer to the coast than those situated further inland. In many cases, homes near the water will need supplemental flood insurance."]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "What To Consider Before You Make an Offer"
    }), "\n", _jsx(_components.p, {
      children: "Take a look at the house’s history and make note of any potential trouble spots."
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Check the house’s loss history report."
        }), " You can obtain a loss history report from the current homeowner, which can show when and why past insurance claims were made. If these claims resulted in repairs and updates that further protect the house, it will look more attractive to an insurance provider."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Get an inspection."
        }), " Mortgage lenders will require you to hire a credentialed home inspector for an inspection, who will identify any potential issues and assess the home’s general condition."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Estimate the cost of upkeep on the house."
        }), " You’ll need to factor in the cost of regular maintenance into the price of the house. Any issues that arise from not properly maintaining the home are not covered under standard insurance policies."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Talk to your insurance agent."
        }), " Show the listing to a trusted insurance professional, explain any issues, and get an estimate for the premium."]
      }), "\n"]
    }), "\n", _jsxs(_components.h3, {
      children: ["How To Shop for Home Insurance", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_153406782-1024x683.webp",
        alt: "First-time homebuyer shopping for and comparing home insurance policies"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsx(_components.p, {
      children: "Much like any other major purchase, you’ll want to compare different providers and policies to determine what’s right for your needs and budget. Below are some tips to help ease the process."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Choose the type of coverage you want"
        }), ". There are many types of home insurance to choose from, so it’s good to research the coverage you think you’ll need and cross-reference it with what your mortgage lender requires."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Choose a coverage amount."
        }), " Create a list of your belongings and consult with an insurance agent to ensure you have sufficient coverage to repair or replace your home and personal property in the event of damage or destruction."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Consider supplemental coverages."
        }), " Depending on where you live, you may need to purchase additional types of coverage, like flood insurance, wind and hail insurance, or a separate umbrella policy that provides additional liability coverage."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Choose a deductible."
        }), " The deductible on your policy is the amount you must pay out of pocket before coverage takes effect. The higher the deductible, the lower the premium."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Compare price quotes."
        }), " Compare policies from several providers to find the best price that meets your needs."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Research available discounts."
        }), " Most insurance companies offer a variety of discounts. Figure out which ones you might qualify for."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Avoid any gaps in coverage."
        }), " If you’re renting while you shop for your first home, make sure you’re covered by renters insurance. Keep that policy until you purchase a homeowners policy."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Home Insurance Costs"
    }), "\n", _jsx(_components.p, {
      children: "The average homeowners insurance premium is $2,927 per year or $244 per month, but prices continue to rise. Some states, such as Florida and Louisiana, are expected to see massive spikes in home insurance costs due to the rising frequency of damaging storms and hurricanes. Insurance in high-risk areas is becoming increasingly expensive, as insurers are withdrawing from those markets. Here in North Carolina, we pay an average of $3,237 annually, which is above the national average."
    }), "\n", _jsx(_components.p, {
      children: "Your credit score significantly impacts your premium costs. Nationally, homeowners with poor credit pay an average of $4,638 annually, while those with good credit pay the national average of $2,927, and excellent credit holders pay $2,329 per year. First-time buyers should prioritize improving their credit score before shopping for insurance to secure better rates."
    }), "\n", _jsx(_components.h4, {
      children: "Factors That Affect the Cost of Home Insurance"
    }), "\n", _jsxs(_components.p, {
      children: ["Here are the factors that ", _jsx(_components.a, {
        href: "/home-insurance/home-insurance-statistics",
        children: "affect the cost"
      }), " of a homeowners insurance premium."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Location."
        }), " The location of your home plays a significant role in calculating your home insurance premium. High-risk areas that are prone to flooding, earthquakes, wildfires, or other severe weather have higher premiums. If you live in a rural area, the cost of repairs and labor can increase, making your home costlier to insure."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Dwelling coverage."
        }), " Dwelling coverage is the portion of your policy that protects your home’s physical structure. More dwelling coverage usually means higher premiums."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Deductible."
        }), " The higher the deductible, the lower the premium."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Age of the home."
        }), " Older homes tend to be more expensive to insure."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Your credit history."
        }), " The higher your credit score, the lower risk you pose to the insurer, and therefore the lower your premiums will be."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Your claims history."
        }), " Insurers will take your past claims history into account when calculating your premiums."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Your marital status."
        }), " Married couples tend to pay lower premiums than single homeowners."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Home Insurance Mistakes To Avoid"
    }), "\n", _jsxs(_components.p, {
      children: ["First-time homeowners often make mistakes with homeowners insurance, which can end up costing a lot of money. Here are five ", _jsx(_components.a, {
        href: "https://www.iii.org/article/five-insurance-mistakes-avoid-and-still-save-money",
        children: "common mistakes"
      }), " to avoid."]
    }), "\n", _jsx(_components.h4, {
      children: "Insuring Your Home for Its Market Value Rather Than the Cost of Rebuilding"
    }), "\n", _jsx(_components.p, {
      children: "Home insurance is designed to cover the cost of rebuilding a home, not its market value or sale price. Some homeowners, seeing housing prices decrease, respond by cutting their coverage to save money, but this can put them in a vulnerable position. If an accident occurs, your policy may not cover all the costs of repairing damage and replacing belongings."
    }), "\n", _jsx(_components.h4, {
      children: "Waiting Too Long To File a Claim"
    }), "\n", _jsx(_components.p, {
      children: "It’s best practice to file a claim as soon as you can after an incident. The quicker you file, the easier it will be for your insurer to help pay for lodging or recommend a trusted contractor to begin repairs. You don’t want to be stuck in an unlivable home with no timeframe on rebuilding."
    }), "\n", _jsx(_components.h4, {
      children: "Not Realizing Flood Insurance Isn’t Standard"
    }), "\n", _jsxs(_components.p, {
      children: ["Standard homeowners insurance policies do not include flood insurance. However, ", _jsx(_components.a, {
        href: "/home-insurance/flood-insurance",
        children: "41%"
      }), " of our surveyed homeowners believed it did. If you live in an area prone to flooding, it’s a good idea to purchase separate flood coverage through the National Flood Insurance Program (NFIP), which is run by FEMA, or from a third-party provider. In some cases, your mortgage lender may require you to purchase the coverage."]
    }), "\n", _jsx(_components.h4, {
      children: "Not Maintaining a Home Inventory"
    }), "\n", _jsx(_components.p, {
      children: "Keeping an up-to-date inventory of all the belongings in your home will help you be more accurate when it’s time to file a claim. Big-ticket items like appliances are easy to remember, but smaller items in storage or tucked in the corners of rooms can add thousands to your claim — your insurer is required by law to cover all the items in your home."
    }), "\n", _jsx(_components.h4, {
      children: "Choosing an Insurer Based on Price Alone"
    }), "\n", _jsx(_components.p, {
      children: "Just because a policy is cheap doesn’t mean it’s worth it. If a company isn’t financially stable, you may not see the deserved payout on your claim. When researching providers, check their financial status by looking at ratings from institutions like AM Best or Moody’s."
    }), "\n", _jsx(_components.h3, {
      children: "How To Make Your First Claim"
    }), "\n", _jsxs(_components.p, {
      children: ["Making your ", _jsx(_components.a, {
        href: "/home-insurance/how-to-file-home-insurance-claim",
        children: "first claim"
      }), " can seem daunting, but it’s a fairly straightforward process. Here’s the breakdown:"]
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Contact your insurance provider immediately through the online portal or phone line."
        }), " The longer you wait, the more difficult some of the other steps may become."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Thoroughly document the damage."
        }), " Take lots of photos and write down exactly what needs to be repaired or replaced."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Make temporary repairs if necessary."
        }), " If you can’t leave your house after an incident, or if there is imminent danger due to damage, make and document temporary repairs to ensure you and your family’s safety."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Communicate with your insurance adjuster."
        }), " Keep your insurance adjuster informed of the process. Detail all damage, provide receipts for expenses, and answer any questions they have."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Save all your receipts."
        }), " To be reimbursed for repairs, lodging, and other living expenses, you’ll need to present proof of the money you spent to the adjuster. Save and report every receipt, no matter how small."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Compare contractors."
        }), " Your insurance provider will likely recommend a contractor to conduct repairs, but feel free to compare their suggestion with contractors you find. It doesn’t hurt to obtain several estimates before settling on a contractor."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Use your loss of use coverage to help with living expenses."
        }), " Loss of use coverage can help you pay for lodging and meals if your home is unsafe."]
      }), "\n"]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
